import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import {
  ConsultantsErrorFallback,
  ConsultantsPage,
} from '@/pages/ConsultantsPage'
import { PageLoader } from '@/components/shared/PageLoader'
import { fetchCandidates } from '@/services/api/resources.ts'
import { requireRoles } from '@/utils/routeGuards.ts'
import { ROUTE_ACCESS } from '@/constants/routeAccess.ts'
import { ROUTES } from '@/constants/routeConstants'

const CONSULTANTS_PAGE_SIZE = 12

export const consultantsSearchSchema = z.object({
  page: z.number().positive().default(1).catch(1),
  query: z.string().optional(),
  sort: z
    .enum([
      'desired_daily_rate:asc',
      'desired_daily_rate:desc',
      'desired_salary:asc',
      'desired_salary:desc',
      'availability:asc',
      'availability:desc',
      'last_name:asc',
      'last_name:desc',
      'first_name:asc',
      'first_name:desc',
    ])
    .default('last_name:asc'),
  typeContract: z.enum(['cdi', 'freelance']).optional(),
  desiredDailyRateMin: z.number().optional(),
  desiredDailyRateMax: z.number().optional(),
  desiredSalaryMin: z.number().optional(),
  desiredSalaryMax: z.number().optional(),
  cleverscoreMin: z.number().optional(),
  experiences: z.array(z.string()).optional(),
  skills: z.array(z.number()).optional(),
  city: z.string().optional(),
  remoteOnly: z.boolean().optional(),
  favoritesOnly: z.boolean().optional(),
  availabilities: z.array(z.number()).optional(),
})

export const Route = createFileRoute('/consultants/')({
  beforeLoad: requireRoles(ROUTE_ACCESS[ROUTES.CONSULTANTS_LIST]),
  validateSearch: consultantsSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const {
      page,
      query,
      sort,
      typeContract,
      desiredDailyRateMin,
      desiredDailyRateMax,
      desiredSalaryMin,
      desiredSalaryMax,
      cleverscoreMin,
      experiences,
      skills,
      city,
      remoteOnly,
      favoritesOnly,
      availabilities,
    } = deps
    const defaultLimit = CONSULTANTS_PAGE_SIZE
    const offset = (page - 1) * defaultLimit

    return fetchCandidates({ offset, limit: defaultLimit }, query, sort, {
      typeContract,
      desiredDailyRateMin:
        typeContract !== 'cdi' ? desiredDailyRateMin : undefined,
      desiredDailyRateMax:
        typeContract !== 'cdi' ? desiredDailyRateMax : undefined,
      desiredSalaryMin:
        typeContract !== 'freelance' ? desiredSalaryMin : undefined,
      desiredSalaryMax:
        typeContract !== 'freelance' ? desiredSalaryMax : undefined,
      cleverscoreMin,
      experiences,
      skills,
      city,
      remoteOnly,
      favoritesOnly,
      availabilities,
    })
  },
  component: ConsultantsPage,
  pendingComponent: PageLoader,
  errorComponent: ConsultantsErrorFallback,
})
